/**
 * Per-ability cooldown bookkeeping. Pure and frame-driven (decremented by the
 * real frame dt, like the slow-mo trainer) so cooldowns feel consistent to the
 * player regardless of slow-motion. Keyed by an arbitrary ability id string.
 */
export class AbilityTracker {
  private readonly remainingById = new Map<string, number>();

  /** Clear every cooldown (on respawn or hero switch — abilities come back ready). */
  reset(): void {
    this.remainingById.clear();
  }

  /** Advance all cooldowns by `dt` seconds, dropping any that have elapsed. */
  tick(dt: number): void {
    for (const [id, left] of this.remainingById) {
      const next = left - dt;
      if (next <= 0) {
        this.remainingById.delete(id);
      } else {
        this.remainingById.set(id, next);
      }
    }
  }

  /** True when `id` is off cooldown and can be triggered. */
  ready(id: string): boolean {
    return !this.remainingById.has(id);
  }

  /** Seconds left on `id`'s cooldown (0 when ready). */
  remaining(id: string): number {
    return this.remainingById.get(id) ?? 0;
  }

  /** Put `id` on cooldown for `seconds`. */
  trigger(id: string, seconds: number): void {
    if (seconds > 0) {
      this.remainingById.set(id, seconds);
    }
  }

  /**
   * Shorten every running cooldown by `seconds` (a passive surge). Cooldowns
   * that drop to zero come off cooldown immediately.
   */
  hasten(seconds: number): void {
    if (seconds <= 0) {
      return;
    }
    for (const [id, left] of this.remainingById) {
      const next = left - seconds;
      if (next <= 0) {
        this.remainingById.delete(id);
      } else {
        this.remainingById.set(id, next);
      }
    }
  }
}
